// Lowercase, no accents, no punctuation.
const norm = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9\s]/g, ' ');

// "swimm" → "swim", "stopp" → "stop"; keeps "ss" and "ll" words like "dress" and "fill".
const undouble = (w) => (/([bdgmnprt])\1$/.test(w) ? w.slice(0, -1) : w);

// Light stemming so "shipping" finds "shipped", "charge" finds "charging" and "batteries" finds "battery".
const stem = (w) => {
  if (w.length > 4 && w.endsWith('ies')) return w.slice(0, -3) + 'y';
  if (w.length > 5 && w.endsWith('ing')) return undouble(w.slice(0, -3));
  if (w.length > 4 && w.endsWith('ed')) return undouble(w.slice(0, -2));
  if (w.length > 4 && w.endsWith('es')) return w.slice(0, -2);
  if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
  if (w.length > 4 && w.endsWith('e')) return w.slice(0, -1);
  return w;
};

const words = (s) => norm(s).split(/\s+/).filter(Boolean);

// Question words people type that say nothing about the topic.
const STOP = new Set('a an and are be can could do does for from get how i if in into is it its me my of on or should so the their them there this to was we what when where which who why will with would you your after about just still long much many'.split(' '));

// Typed words that carry meaning; a query of only stop words keeps them all.
const terms = (raw) => {
  const all = words(raw);
  const keep = all.filter((w) => !STOP.has(w));
  return (keep.length ? keep : all).map(stem);
};

const hits = (tokens, t) => tokens.some((k) => k.startsWith(t));

// Edit distance, given up once it passes max.
const near = (a, b, max) => {
  if (Math.abs(a.length - b.length) > max) return false;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    if (Math.min(...row) > max) return false;
    prev = row;
  }
  return prev[b.length] <= max;
};

// Emails and phone numbers never leave the browser.
const scrub = (raw) => raw.replace(/\S+@\S+/g, '[email]').replace(/\+?\d[\d\s().-]{5,}\d/g, '[number]').slice(0, 120);
const key = (q) => norm(q).replace(/\s+/g, ' ').trim();

class BrFaq extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-search-form]');
    this.input = this.querySelector('[data-search]');
    this.status = this.querySelector('[data-status]');
    this.panel = this.querySelector('[data-results]');
    this.nav = this.querySelector('[data-nav]');
    this.list = this.querySelector('[data-topics]');
    this.pill = this.querySelector('[data-pill]');
    this.links = [...this.querySelectorAll('[data-topic-link]')];
    this.groups = [...this.querySelectorAll('[data-group]')];

    this.items = [...this.querySelectorAll('[data-item]')].map((el) => {
      const q = el.querySelector('[data-q]').textContent.trim();
      const group = el.closest('[data-group]');
      const box = el.querySelector('.br-faq__answer');
      // Text nodes joined with spaces, so list items like "charger" and "USB" stay separate words.
      const answer = box ? document.evaluate('.//text()', box, null, 7, null) : null;
      const text = answer ? Array.from({ length: answer.snapshotLength }, (_, i) => answer.snapshotItem(i).nodeValue).join(' ') : '';
      return {
        el,
        q,
        group,
        topic: group?.querySelector('[id$="-h"]')?.textContent.trim() || '',
        qTokens: words(q).map(stem),
        tokens: words(`${q} ${text} ${el.dataset.keywords || ''}`).map(stem),
      };
    });

    // Other pages the editor named, plus Shopify's own search, list under the answers.
    this.pages = [...this.querySelectorAll('[data-page]')].map((el) => ({
      title: el.dataset.title,
      url: el.dataset.url,
      kind: 'Page',
      tokens: words(`${el.dataset.title} ${el.dataset.keywords}`).map(stem),
    }));
    this.suggest = this.form?.dataset.suggest;
    this.log = this.form?.dataset.missLog;
    this.country = this.form?.dataset.country || '';
    this.sent = new Set();
    this.queue = [];
    this.counts = { searches: 0, opens: 0 };
    this.more = this.querySelector('[data-more]');

    this.header = document.querySelector('.mount-header, .shopify-section-group-header-group');
    this.measure();
    this.resize = new ResizeObserver(() => { this.measure(); this.spy(); });
    this.resize.observe(this);
    if (this.header) this.resize.observe(this.header);

    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.spy(); });
    };
    addEventListener('scroll', this.onScroll, { passive: true });

    this.links.forEach((a) => a.addEventListener('click', (e) => this.go(e, a)));

    // Each opened answer is counted, with how the reader got there; the first one after a search answers that search.
    this.items.forEach((it) => it.el.addEventListener('toggle', () => {
      if (!it.el.open) return;
      if (this.lastSearch && !this.lastSearch.picked) this.lastSearch.picked = it.q;
      this.track({ type: 'open', question: it.q, topic: it.topic, via: this.via || 'click' });
      this.via = null;
    }));

    if (this.input) {
      // Enter goes to the best answer; with none, the reader stays on this page.
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (this.input.value.trim() !== this.lastRaw) this.search();
        this.settle();
        if (this.results?.length) this.pick(this.results[0]);
      });
      this.input.addEventListener('input', () => this.search());
      this.input.addEventListener('focus', () => { if (this.input.value.trim()) this.search(); });
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' && !this.panel.hidden) { e.preventDefault(); this.targets()[0]?.focus(); }
        if (e.key === 'Escape') { if (!this.panel.hidden) this.close(); else if (this.input.value) { this.input.value = ''; this.search(); } }
      });
      this.panel.addEventListener('keydown', (e) => this.keys(e));
      // Safari doesn't focus a clicked link, so keep focus in the box until the click lands.
      this.panel.addEventListener('pointerdown', (e) => { if (e.target.closest('a')) e.preventDefault(); });
      this.panel.addEventListener('click', (e) => {
        const a = e.target.closest('a[data-result]');
        if (!a) return;
        e.preventDefault();
        this.pick(this.results[Number(a.dataset.result)]);
      });
      // Results close when focus or a click leaves the search box; the typed words stay.
      this.form.addEventListener('focusout', (e) => { if (!this.form.contains(e.relatedTarget)) { this.close(); this.settle(); } });
    }

    this.onHash = () => this.reveal(location.hash);
    addEventListener('hashchange', this.onHash);
    this.onHide = () => { this.settle(); this.flush(true); };
    addEventListener('pagehide', this.onHide);
    this.onVis = () => { if (document.visibilityState === 'hidden') this.onHide(); };
    document.addEventListener('visibilitychange', this.onVis);
    if (location.hash) requestAnimationFrame(() => this.reveal(location.hash, true));

    this.spy();
    requestAnimationFrame(() => this.nav?.setAttribute('data-ready', ''));
  }

  disconnectedCallback() {
    removeEventListener('scroll', this.onScroll);
    removeEventListener('hashchange', this.onHash);
    removeEventListener('pagehide', this.onHide);
    document.removeEventListener('visibilitychange', this.onVis);
    this.resize?.disconnect();
    this.cancel?.();
  }

  get reduced() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Header bottom, read live: the theme header can turn sticky after this script runs.
  headerBottom() {
    const h = this.header;
    if (!h) return 0;
    const pos = getComputedStyle(h).position;
    return pos === 'sticky' || pos === 'fixed' ? Math.max(0, Math.round(h.getBoundingClientRect().bottom)) : 0;
  }

  // Scroll lands below the sticky header and, on phones, below the topic row.
  measure() {
    this.top = this.headerBottom();
    this.style.setProperty('--br-faq-top', `${this.top}px`);
    const stacked = this.nav && getComputedStyle(this.list).flexDirection === 'row';
    this.bar = stacked ? this.nav.offsetHeight : 0;
    this.style.setProperty('--br-faq-bar', `${this.bar}px`);
    // The title sticks under the header on wide layouts; answers land below it.
    this.titleEl = this.titleEl || this.querySelector('.br-faq__title');
    this.head = this.titleEl && getComputedStyle(this.titleEl).position === 'sticky' ? this.titleEl.offsetHeight : 0;
    this.style.setProperty('--br-faq-head', `${this.head}px`);
    const cols = this.querySelector('.br-faq__columns');
    this.gap = parseFloat(cols && getComputedStyle(cols).getPropertyValue('--faq-gap')) || 24;
  }

  // The line a group crosses to become current: just under the header, the topic row and the gap.
  line() {
    const top = this.headerBottom();
    if (top !== this.top) this.measure();
    return this.top + this.head + this.bar + this.gap;
  }

  spy() {
    if (this.lock) return;
    this.unhash();
    if (!this.links.length) return;
    // A picked answer near the page end can't reach the line; its topic stays lit until the reader scrolls away.
    if (this.pinned) {
      if (Math.abs(scrollY - this.pinY) < 4) { this.activate(this.pinned); return; }
      this.pinned = null;
    }
    const shown = this.groups.filter((g) => !g.hidden);
    if (!shown.length) return;
    const line = this.line() + 8;
    let cur = shown[0];
    for (const g of shown) if (g.getBoundingClientRect().top <= line) cur = g;
    // At the bottom of the page the last group can never reach the line.
    const atEnd = innerHeight + scrollY >= document.documentElement.scrollHeight - 2;
    const lastShown = shown[shown.length - 1];
    if (atEnd && lastShown.getBoundingClientRect().top < innerHeight) cur = lastShown;
    this.activate(cur);
  }

  // Once the linked answer or topic leaves the screen, the address drops its #anchor so a shared link isn't stale.
  unhash() {
    const hash = location.hash;
    if (hash.length < 2 || this.pinned) return;
    let el;
    try { el = this.querySelector(hash); } catch { return; }
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.bottom < this.top || r.top > innerHeight) history.replaceState(history.state, '', location.pathname + location.search);
  }

  activate(group) {
    const link = this.links.find((a) => a.hash === `#${group?.id}`);
    if (!link) return;
    if (link !== this.current) {
      this.links.forEach((a) => (a === link ? a.setAttribute('aria-current', 'true') : a.removeAttribute('aria-current')));
      this.current = link;
      if (this.bar) {
        const pad = 16;
        const { offsetLeft: l, offsetWidth: w } = link;
        const { scrollLeft: s, clientWidth: cw } = this.list;
        if (l < s + pad || l + w > s + cw - pad) {
          this.list.scrollTo({ left: l - pad, behavior: this.reduced ? 'auto' : 'smooth' });
        }
      }
    }
    this.place(link);
  }

  place(link) {
    if (!link || !this.pill) return;
    // The pill lives in the chip row, so it scrolls with the chips instead of chasing them.
    const x = link.offsetLeft;
    const y = link.offsetTop;
    this.pill.style.setProperty('--pill-x', `${x}px`);
    this.pill.style.setProperty('--pill-y', `${y}px`);
    this.pill.style.setProperty('--pill-w', `${link.offsetWidth}px`);
    this.pill.style.setProperty('--pill-h', `${link.offsetHeight}px`);
  }

  go(e, link) {
    const group = this.querySelector(link.hash);
    if (!group) return;
    e.preventDefault();
    this.lock = group;
    this.activate(group);
    history.replaceState(null, '', link.hash);
    group.querySelector('[id$="-h"]')?.focus({ preventScroll: true });
    this.glide(this.targetY(group), () => { this.lock = null; this.spy(); });
  }

  targetY(el) {
    const y = el.getBoundingClientRect().top + scrollY - this.line();
    return Math.max(0, Math.min(y, document.documentElement.scrollHeight - innerHeight));
  }

  // Critically damped spring on the page scroll (Apple: damping 1, response 0.5s); a wheel, touch or key hands it back.
  glide(to, done) {
    this.cancel?.();
    const root = document.documentElement;
    if (this.reduced) {
      scrollTo(0, to);
      requestAnimationFrame(() => done?.());
      return;
    }
    const prev = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    const omega = (2 * Math.PI) / 0.5;
    const d0 = scrollY - to;
    const t0 = performance.now();
    let raf = 0;
    const stop = () => finish();
    const opts = { passive: true };
    const finish = () => {
      cancelAnimationFrame(raf);
      removeEventListener('wheel', stop, opts);
      removeEventListener('touchstart', stop, opts);
      removeEventListener('keydown', stop);
      root.style.scrollBehavior = prev;
      if (this.cancel === finish) this.cancel = null;
      done?.();
    };
    const frame = (now) => {
      const t = Math.max(0, now - t0) / 1000;
      const d = d0 * (1 + omega * t) * Math.exp(-omega * t);
      if (Math.abs(d) < 0.5) { scrollTo(0, to); finish(); return; }
      scrollTo(0, to + d);
      raf = requestAnimationFrame(frame);
    };
    addEventListener('wheel', stop, opts);
    addEventListener('touchstart', stop, opts);
    addEventListener('keydown', stop);
    this.cancel = finish;
    raf = requestAnimationFrame(frame);
  }

  // A link to a question opens it; a link to a topic just lands on it.
  reveal(hash, first = false) {
    if (!hash || hash.length < 2) return;
    let el;
    try { el = this.querySelector(hash); } catch { return; }
    if (!el) return;
    if (el.matches('[data-item]')) {
      if (!el.open) this.via = 'link';
      el.open = true;
    }
    this.measure();
    scrollTo(0, this.targetY(el));
    if (first) this.spy();
  }

  // Each typed word not on the page swaps for the closest page word: one letter off from 4 letters, two from 7.
  respell(t) {
    this.vocab ||= [...new Set(this.items.flatMap((it) => it.tokens))].filter((w) => w.length > 2);
    return t.map((w) => {
      if (w.length < 4 || this.items.some((it) => hits(it.tokens, w))) return w;
      return this.vocab.find((v) => near(w, v, 1)) || (w.length >= 7 && this.vocab.find((v) => near(w, v, 2))) || w;
    });
  }

  // Ranks every answer for the typed words; the page itself never changes.
  rank(t) {
    // Rare words count most (IDF): "swimming" says more than "working".
    const N = this.items.length;
    const weight = new Map(t.map((w) => [w, Math.log(1 + N / Math.max(1, this.items.filter((it) => hits(it.tokens, w)).length))]));
    const scored = this.items.map((it) => {
      let n = 0;
      let score = 0;
      t.forEach((w) => {
        if (!hits(it.tokens, w)) return;
        n += 1;
        // A word in the question counts double; a whole-word match beats a word that only starts the same.
        // A word repeated in an answer nudges it ahead of one that mentions it once.
        const tf = it.tokens.filter((k) => k.startsWith(w)).length;
        score += weight.get(w) * (hits(it.qTokens, w) ? 2 : it.tokens.includes(w) ? 1.5 : 1) * (1 + 0.1 * Math.min(tf - 1, 3));
      });
      return { it, n, score };
    }).filter((x) => x.n);
    const exact = scored.filter((x) => x.n === t.length);
    const top = Math.max(0, ...scored.map((x) => x.score));
    // No answer has every word: offer the ones scoring close to the best.
    const list = exact.length ? exact : scored.filter((x) => x.score >= top * 0.6);
    list.sort((a, b) => b.score - a.score);
    return { exact: exact.length > 0, list: list.slice(0, 6).map((x) => x.it), total: list.length };
  }

  search() {
    const raw = this.input.value.trim();
    let t = terms(raw);
    clearTimeout(this.say);
    if (!t.length) {
      this.lastRaw = '';
      this.results = [];
      this.others = [];
      this.abort?.abort();
      this.queueSearch(null);
      this.close();
      this.say = setTimeout(() => { this.status.textContent = this.dataset.statusAll; }, 400);
      return;
    }

    this.lastRaw = raw;
    let { exact, list, total } = this.rank(t);
    // Nothing found: try the page's own words that are one typo away ("batery" finds "battery").
    if (!list.length) {
      const fixed = this.respell(t);
      if (fixed.join() !== t.join()) {
        ({ list, total } = this.rank(fixed));
        exact = false;
        t = fixed;
      }
    }
    this.results = list;
    const ul = this.panel.querySelector('[data-results-list]');
    ul.replaceChildren(...list.map((it, i) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.className = 'br-faq__result';
      a.href = `#${it.el.id}`;
      a.dataset.result = String(i);
      const q = document.createElement('span');
      q.className = 'br-faq__result-q';
      q.append(this.mark(it.q, t));
      const topic = document.createElement('span');
      topic.className = 'br-faq__result-topic';
      topic.textContent = it.topic;
      a.append(q, ' ', topic);
      li.append(a);
      return li;
    }));
    this.panel.querySelector('[data-results-head]').textContent = !list.length ? `No answers for “${raw}”` : exact ? 'Best matches' : 'No exact match. Closest answers:';
    this.panel.hidden = false;
    this.answerNote = { raw, exact, n: list.length, total };
    this.queueSearch({ raw, result: !list.length ? 'none' : exact ? 'answered' : 'closest' });

    const local = this.pages.filter((pg) => t.every((w) => hits(pg.tokens, w)));
    this.showOthers(local);
    this.announce();
    this.fetchOthers(raw, local);
  }

  // The list updates as you type; the announcement waits until typing pauses.
  announce() {
    const { raw, exact, n, total } = this.answerNote;
    const m = this.others?.length || 0;
    const text = (!n
      ? `No answers for “${raw}”.`
      : `${exact ? '' : 'No exact match. '}${n} ${exact ? '' : 'closest '}${n === 1 ? 'answer' : 'answers'} for “${raw}”${total > n ? `, top ${n} shown` : ''}.`)
      + (m ? ` ${m} ${m === 1 ? 'page' : 'pages'} elsewhere on the site.` : '')
      + (n || m ? ' Press Enter for the best, or Down arrow to choose.' : '');
    clearTimeout(this.say);
    this.say = setTimeout(() => { this.status.textContent = text; }, 400);
  }

  // Shopify's search for pages, products and posts; waits for a typing pause and drops stale replies.
  fetchOthers(raw, local) {
    clearTimeout(this.wait);
    this.abort?.abort();
    if (!this.suggest) return;
    this.wait = setTimeout(async () => {
      this.abort = new AbortController();
      const q = new URLSearchParams({
        q: raw,
        'resources[type]': 'page,product,article',
        'resources[limit]': '4',
        'resources[options][fields]': 'title,body,product_type,tag,vendor',
      });
      try {
        const res = await fetch(`${this.suggest}.json?${q}`, { signal: this.abort.signal });
        if (!res.ok) return;
        const r = (await res.json()).resources?.results || {};
        const kinds = [['pages', 'Page'], ['products', 'Product'], ['articles', 'Blog post']];
        const found = kinds.flatMap(([key, kind]) => (r[key] || []).map((x) => ({ title: x.title, url: x.url, kind })));
        if (this.input.value.trim() !== raw) return;
        this.showOthers([...local, ...found]);
        this.announce();
      } catch {
        // Offline or aborted: the answers and editor-picked pages still show.
      }
    }, 250);
  }

  // One row per page, no repeats, never this page itself; capped at five.
  showOthers(list) {
    if (!this.more) return;
    const here = location.pathname;
    const seen = new Set([here]);
    this.others = list.filter((o) => {
      let path;
      try { path = new URL(o.url, location.origin).pathname; } catch { return false; }
      if (seen.has(path)) return false;
      seen.add(path);
      return true;
    }).slice(0, 5);
    this.more.querySelector('[data-more-list]').replaceChildren(...this.others.map((o) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.className = 'br-faq__result';
      a.href = o.url;
      const title = document.createElement('span');
      title.className = 'br-faq__result-q';
      title.textContent = o.title;
      const kind = document.createElement('span');
      kind.className = 'br-faq__result-topic';
      kind.textContent = o.kind;
      a.append(title, ' ', kind);
      li.append(a);
      return li;
    }));
    this.more.hidden = this.others.length === 0;
  }

  close() {
    if (this.panel) this.panel.hidden = true;
  }

  // Each search is saved once typing pauses, so the team sees what people ask and what the FAQ misses.
  queueSearch(note) {
    clearTimeout(this.searchWait);
    this.pending = this.log ? note : null;
    if (this.pending) this.searchWait = setTimeout(() => this.settle(), 2000);
  }

  settle() {
    clearTimeout(this.searchWait);
    const note = this.pending;
    this.pending = null;
    if (!note) return;
    const q = scrub(note.raw);
    const k = key(q);
    if (k.length < 3 || this.sent.has(k)) return;
    this.sent.add(k);
    this.lastSearch = { type: 'search', q, k, result: note.result, pages: this.others?.length || 0 };
    this.track(this.lastSearch);
  }

  // Stats wait for the visitor's analytics consent where the store asks for it.
  allowed() {
    return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== false;
  }

  track(ev) {
    if (!this.log || !this.allowed()) return;
    if (this.queue.length >= 20) this.flush();
    ev.t = Date.now();
    this.queue.push(ev);
    if (ev.type === 'search') this.counts.searches += 1;
    if (ev.type === 'open') this.counts.opens += 1;
  }

  device() {
    return matchMedia('(max-width: 749px)').matches ? 'Phone' : matchMedia('(max-width: 989px)').matches ? 'Tablet' : 'Desktop';
  }

  // Only the site name the visitor came from, never the full address.
  from() {
    try { return new URL(document.referrer).hostname.replace(/^www\./, '') || 'Direct'; } catch { return 'Direct'; }
  }

  // Events go in one batch when the page is hidden; a visit row is added the first time.
  flush(leaving = false) {
    if (!this.log) return;
    if (leaving && !this.visited && this.allowed()) {
      this.visited = true;
      this.queue.unshift({ type: 'visit', t: Date.now(), from: this.from(), ...this.counts });
    }
    if (!this.queue.length) return;
    const events = this.queue.splice(0).map(({ k, ...ev }) => ev);
    const body = JSON.stringify({ v: 2, country: this.country, device: this.device(), path: location.pathname, events });
    // Plain text skips the CORS preflight that Google Apps Script can't answer.
    if (!navigator.sendBeacon?.(this.log, new Blob([body], { type: 'text/plain' }))) {
      fetch(this.log, { method: 'POST', mode: 'no-cors', keepalive: true, body }).catch(() => {});
    }
  }

  // Links in the open results, in order.
  targets() {
    return [...this.panel.querySelectorAll('a')].filter((a) => !a.hidden && !a.closest('[hidden]'));
  }

  // Up and Down move through the results; Up from the first goes back to the box; Escape closes.
  keys(e) {
    const all = this.targets();
    const i = all.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); all[Math.min(i + 1, all.length - 1)]?.focus(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); (i <= 0 ? this.input : all[i - 1]).focus(); }
    if (e.key === 'Home') { e.preventDefault(); all[0]?.focus(); }
    if (e.key === 'End') { e.preventDefault(); all[all.length - 1]?.focus(); }
    if (e.key === 'Escape') { e.preventDefault(); this.close(); this.input.focus(); }
  }

  pick(it) {
    if (!it) return;
    this.close();
    this.settle();
    if (this.lastSearch?.k === key(scrub(this.lastRaw || '')) && !this.lastSearch.picked) this.lastSearch.picked = it.q;
    if (!it.el.open) this.via = 'search';
    it.el.open = true;
    history.replaceState(null, '', `#${it.el.id}`);
    this.lock = it.group;
    this.activate(it.group);
    it.el.querySelector('summary').focus({ preventScroll: true });
    this.glide(this.targetY(it.el), () => { this.lock = null; this.pinned = it.group; this.pinY = scrollY; this.spy(); });
  }

  mark(text, t) {
    const frag = document.createDocumentFragment();
    text.split(/(\s+)/).forEach((part) => {
      const w = stem(norm(part).trim());
      if (part.trim() && t.some((x) => w.startsWith(x))) {
        const m = document.createElement('mark');
        m.textContent = part;
        frag.append(m);
      } else {
        frag.append(part);
      }
    });
    return frag;
  }
}

if (!customElements.get('br-faq')) {
  customElements.define('br-faq', BrFaq);
}
